import { useLocalization } from "../../../features/localization/index.js";
import DataTable_default from "../../../components/data-table/DataTable.vue.js";
import DataTableHeader_default from "../../../components/data-table/DataTableHeader.vue.js";
import DataTableRow_default from "../../../components/data-table/DataTableRow.vue.js";
import RequestTableRow_default from "./RequestTableRow.vue.js";
import { Fragment, computed, createBlock, createElementBlock, createTextVNode, createVNode, defineComponent, openBlock, renderList, toDisplayString, unref, withCtx } from "vue";
//#region src/v2/blocks/request-block/components/RequestTable.vue?vue&type=script&setup=true&lang.ts
var RequestTable_vue_vue_type_script_setup_true_lang_default = /*@__PURE__*/ defineComponent({
	__name: "RequestTable",
	props: {
		data: {},
		deferKeyUpdates: { type: Boolean },
		hasCheckboxDisabled: { type: Boolean },
		invalidParams: {},
		label: {},
		showUploadButton: { type: Boolean },
		showAddRowPlaceholder: {
			type: Boolean,
			default: true
		},
		environment: {}
	},
	emits: [
		"upsertRow",
		"deleteRow",
		"uploadFile",
		"removeFile",
		"navigate",
		"selectPreset"
	],
	setup(__props, { expose: __expose, emit: __emit }) {
		/**
		* Make this component more generic that can be used also for the operation body
		*/
		const emit = __emit;
		const { translate } = useLocalization();
		const columns = computed(() => {
			if (__props.showUploadButton) return [
				"36px",
				"",
				"",
				"minmax(0, 1fr)"
			];
			return [
				"36px",
				"",
				""
			];
		});
		/** Add the last empty row (for ui purposes only) */
		const displayData = computed(() => {
			if (!__props.showAddRowPlaceholder) return __props.data;
			const last = __props.data.at(-1);
			if (last?.name !== "" || last.value !== "") return [...__props.data, {
				name: "",
				value: "",
				isDisabled: true
			}];
			return __props.data;
		});
		/**
		* Stable identity key for each row so Vue never reuses a RequestTableRow instance for a different
		* parameter or the appended placeholder row. Parameter rows are keyed by their parameter identity —
		* the name plus the value path for expanded object parameters. The parts are combined through
		* JSON.stringify so the name/path boundary is unambiguous (for example `ab` + `['c']` never
		* collides with `a` + `['bc']`). Form rows use the name and its occurrence so repeated
		* multipart fields remain distinct while unrelated rows can move without losing their identity.
		*/
		const getRowKey = (row, index) => {
			if (__props.deferKeyUpdates) return `body:${index}`;
			if (row.originalParameter) return JSON.stringify([row.originalParameter.name, ...row.sourceParameterValuePath ?? []]);
			const occurrence = displayData.value.slice(0, index).filter((other) => !other.originalParameter && other.name === row.name).length;
			return `row:${JSON.stringify([row.name, occurrence])}`;
		};
		/** Keep an edited row mounted when saving it changes its parameter identity. */
		const pendingUpdates = /* @__PURE__ */ new Map();
		const rowRefs = /* @__PURE__ */ new Map();
		const setRowRef = (key, element) => {
			if (element) rowRefs.set(key, element);
			else rowRefs.delete(key);
		};
		const matchesPendingUpdate = (key, row) => {
			const update = pendingUpdates.get(key);
			return update?.name === row.name && update.value === row.value;
		};
		const keyedRows = computed((previous = []) => {
			const available = new Set(previous);
			const savedIdentities = new Set(__props.data.map(getRowKey));
			const rows = displayData.value.map((row, index) => {
				const identity = getRowKey(row, index);
				const existing = [...available].find((entry) => entry.identity === identity);
				const pending = [...available].find((entry) => !savedIdentities.has(entry.identity) && !entry.data.sourceParameterValuePath && matchesPendingUpdate(entry.key, row));
				const match = existing ?? pending;
				if (match) {
					available.delete(match);
					if (matchesPendingUpdate(match.key, row)) pendingUpdates.delete(match.key);
				}
				return {
					data: row,
					identity,
					key: match?.key ?? Symbol()
				};
			});
			for (const removed of available) pendingUpdates.delete(removed.key);
			return rows;
		});
		const handleUpsertRow = (row, index, payload) => {
			pendingUpdates.set(row.key, payload);
			emit("upsertRow", index, payload);
		};
		__expose({ focusValue: (index) => {
			const row = keyedRows.value[index];
			if (row) rowRefs.get(row.key)?.focusValue();
		} });
		return (_ctx, _cache) => {
			return openBlock(), createBlock(unref(DataTable_default), {
				class: "group/table flex-1",
				columns: columns.value
			}, {
				default: withCtx(() => [createVNode(unref(DataTableRow_default), { class: "sr-only !block" }, {
					default: withCtx(() => [
						createVNode(unref(DataTableHeader_default), null, {
							default: withCtx(() => [createTextVNode(toDisplayString(__props.label) + " " + toDisplayString(unref(translate)("apiClient.requestTable.enabled")), 1)]),
							_: 1
						}),
						createVNode(unref(DataTableHeader_default), null, {
							default: withCtx(() => [createTextVNode(toDisplayString(__props.label) + " " + toDisplayString(unref(translate)("apiClient.requestTable.key")), 1)]),
							_: 1
						}),
						createVNode(unref(DataTableHeader_default), null, {
							default: withCtx(() => [createTextVNode(toDisplayString(__props.label) + " " + toDisplayString(unref(translate)("apiClient.requestTable.value")), 1)]),
							_: 1
						})
					]),
					_: 1
				}), (openBlock(true), createElementBlock(Fragment, null, renderList(keyedRows.value, (row, index) => {
					return openBlock(), createBlock(RequestTableRow_default, {
						key: row.key,
						ref_for: true,
						ref: (el) => setRowRef(row.key, el),
						data: row.data,
						deferKeyUpdates: __props.deferKeyUpdates,
						environment: __props.environment,
						hasCheckboxDisabled: __props.hasCheckboxDisabled,
						invalidParams: __props.invalidParams,
						label: __props.label,
						showUploadButton: __props.showUploadButton,
						onDeleteRow: ($event) => emit("deleteRow", index),
						onNavigate: _cache[0] || (_cache[0] = (route) => emit("navigate", route)),
						onRemoveFile: ($event) => emit("removeFile", index),
						onSelectPreset: (value) => emit("selectPreset", index, value),
						onUploadFile: ($event) => emit("uploadFile", index),
						onUpsertRow: (payload) => handleUpsertRow(row, index, payload)
					}, null, 8, [
						"data",
						"deferKeyUpdates",
						"environment",
						"hasCheckboxDisabled",
						"invalidParams",
						"label",
						"showUploadButton",
						"onDeleteRow",
						"onRemoveFile",
						"onSelectPreset",
						"onUploadFile",
						"onUpsertRow"
					]);
				}), 128))]),
				_: 1
			}, 8, ["columns"]);
		};
	}
});
//#endregion
export { RequestTable_vue_vue_type_script_setup_true_lang_default as default };

//# sourceMappingURL=RequestTable.vue.script.js.map